import React, { useEffect } from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Link as MuiLink,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { TrendingDown as SubcompraIcon } from "@mui/icons-material";
import { useQuery } from "@tanstack/react-query";
import { Link as RouterLink } from "react-router-dom";

import { usePageTitle } from "../context/PageTitleContext";
import { distributionService } from "../services/distributionService";

/**
 * B3.36 — SubcompraAlertPage
 *
 * Productos/tiendas con patrón crónico de `no_stock_al_momento` en 30
 * días. Se consume directo de `/api/compras/subcompra-alerts` (B3.26
 * backend ampliado). La acción recomendada la genera el backend.
 */
export function SubcompraAlertPage(): React.ReactElement {
  const { setTitle } = usePageTitle();
  const { data, isLoading } = useQuery({
    queryKey: ["distribution", "subcompra-alerts"],
    queryFn: () => distributionService.getSubcompraAlerts(),
    refetchInterval: 5 * 60 * 1000,
  });

  useEffect(() => {
    setTitle("Distribución — Alertas de subcompra");
    return () => setTitle("");
  }, [setTitle]);

  const alerts = data?.alerts ?? [];
  const threshold = data?.threshold_pct ?? 20;

  return (
    <Box sx={{ p: 2 }}>
      <Stack spacing={3}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <SubcompraIcon color="primary" />
          <Typography variant="h4" component="h1" fontWeight={600}>
            Alertas de subcompra
          </Typography>
        </Stack>

        <Alert severity="info" variant="outlined">
          Muestra combinaciones (tienda, producto) con{" "}
          <strong>% de no_stock_al_momento ≥ {threshold}%</strong> en los
          últimos 30 días. La acción recomendada sugiere aplicar{" "}
          <code>factor_correccion</code> al forecast cuando el patrón es
          crónico (≥ 50%).
        </Alert>

        <Paper variant="outlined">
          {isLoading && (
            <Box sx={{ p: 4, display: "flex", justifyContent: "center" }}>
              <CircularProgress />
            </Box>
          )}
          {!isLoading && alerts.length === 0 && (
            <Alert severity="success" sx={{ m: 2 }}>
              Sin alertas de subcompra crónica. El sistema no detecta
              patrones de desabasto frecuentes en los últimos 30 días.
            </Alert>
          )}
          {alerts.length > 0 && (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Tienda</TableCell>
                    <TableCell>Producto</TableCell>
                    <TableCell align="right">Diffs 30d</TableCell>
                    <TableCell align="right">no_stock</TableCell>
                    <TableCell align="right">%</TableCell>
                    <TableCell>Última</TableCell>
                    <TableCell>Acción recomendada</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {alerts.map((a) => (
                    <TableRow
                      key={`${a.store_mxp_code}:${a.unified_product_id}`}
                      hover
                    >
                      <TableCell>{a.store_mxp_code}</TableCell>
                      <TableCell sx={{ fontFamily: "monospace" }}>
                        <MuiLink
                          component={RouterLink}
                          to={`/compras/forecast?search=${encodeURIComponent(
                            a.unified_product_id,
                          )}`}
                          underline="hover"
                        >
                          {a.unified_product_id}
                        </MuiLink>
                      </TableCell>
                      <TableCell align="right">{a.total_diffs_30d}</TableCell>
                      <TableCell align="right">
                        {a.no_stock_count_30d}
                      </TableCell>
                      <TableCell align="right">
                        <Chip
                          size="small"
                          color={a.no_stock_pct >= 50 ? "error" : "warning"}
                          label={`${a.no_stock_pct.toFixed(1)}%`}
                        />
                      </TableCell>
                      <TableCell>
                        {new Date(a.last_occurrence).toLocaleDateString(
                          "es-MX",
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">
                          {a.recommended_action}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Stack>
    </Box>
  );
}
